import { z } from 'zod';
import { Email } from './primitives.js';

/**
 * Password reset and email verification — the request/confirm pairs.
 *
 * Both request bodies are identifier-shaped and both answer the SAME neutral
 * body whether or not the address has an account here: whether an email is
 * registered is not something an unauthenticated caller gets to enumerate,
 * and the rest of the auth surface (dummy Argon2 verifies, one opaque 401 per
 * failure class) is careful about exactly this.
 */
export const PasswordResetRequest = z.object({
  email: Email,
  // Only needed once an email owns more than one household (R9) — the same
  // tiebreaker owner sign-in takes, resolved by the same helper.
  household_code: z.string().max(20).optional(),
});
export type PasswordResetRequest = z.infer<typeof PasswordResetRequest>;

/**
 * The neutral answer. `dev_token` is the raw token, and it is present ONLY
 * outside production: this deployment has no email sender (WhatsApp via
 * Twilio is the single wired outbound channel), so local development and the
 * integration tests would otherwise have no way to reach a token at all.
 */
export const PasswordResetRequestResponse = z.object({
  ok: z.literal(true),
  dev_token: z.string().optional(),
});
export type PasswordResetRequestResponse = z.infer<typeof PasswordResetRequestResponse>;

export const PasswordResetConfirm = z.object({
  token: z.string().min(20).max(200),
  // Same floor as the two invite-accept bodies, which are the other places a
  // password is chosen from outside a session.
  new_password: z.string().min(10).max(128),
});
export type PasswordResetConfirm = z.infer<typeof PasswordResetConfirm>;

export const EmailVerifyRequest = PasswordResetRequest;
export type EmailVerifyRequest = z.infer<typeof EmailVerifyRequest>;

export const EmailVerifyConfirm = z.object({
  token: z.string().min(20).max(200),
});
export type EmailVerifyConfirm = z.infer<typeof EmailVerifyConfirm>;
